import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import HomeRounded from '@mui/icons-material/HomeRounded';
import FolderSpecialRounded from '@mui/icons-material/FolderSpecialRounded';
import ChecklistRounded from '@mui/icons-material/ChecklistRounded';
import ScheduleRounded from '@mui/icons-material/ScheduleRounded';
import AssessmentRounded from '@mui/icons-material/AssessmentRounded';
import AdminPanelSettingsRounded from '@mui/icons-material/AdminPanelSettingsRounded';
import DarkModeRounded from '@mui/icons-material/DarkModeRounded';
import LightModeRounded from '@mui/icons-material/LightModeRounded';
import { useThemeMode } from '../../theme/ThemeModeContext';
import UserMenu from '../../components/layout/UserMenu';
import { useAuthShell } from '../../components/layout/useAuthShell';
import logo from '../../assets/logo.png';
import { usePermission } from '../PmaContext';
import { ActiveTimersIndicator } from './ActiveTimersIndicator';
import { useTimerHeartbeat } from '../api/time-tracking';
import { BRAND_GRADIENT } from './common';

const LINKS = [
  { to: '/pma', label: 'Home', icon: HomeRounded, end: true },
  { to: '/pma/projects', label: 'Projects', icon: FolderSpecialRounded },
  { to: '/pma/tasks', label: 'My Tasks', icon: ChecklistRounded },
  { to: '/pma/timesheet', label: 'Timesheet', icon: ScheduleRounded },
  { to: '/pma/reports', label: 'Reports', icon: AssessmentRounded, perm: 'view_reports' },
  { to: '/pma/admin', label: 'Admin', icon: AdminPanelSettingsRounded, perm: 'access_admin' },
];

/**
 * Chrome for everything under /pma. While inside PMA the main QuillStack menu is replaced by the
 * PMA menu, with a back button to return to the main section.
 */
export default function PmaShell() {
  const { can } = usePermission();
  const location = useLocation();
  const navigate = useNavigate();
  const { mode, toggleMode } = useThemeMode();
  const { user, avatarInitial, go, handleLogout } = useAuthShell();
  useTimerHeartbeat();

  return (
    <Box>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: (t) => t.zIndex.appBar,
          mx: { xs: -2, sm: -3 },
          mt: { xs: -2, md: -3 },
          mb: 2.5,
          px: { xs: 2, sm: 3 },
          py: 1,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          borderImage: `${BRAND_GRADIENT} 1`,
          borderBottomWidth: 3,
          borderBottomStyle: 'solid',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <Button variant="outlined" size="small" startIcon={<ArrowBackRounded />} onClick={() => navigate('/dashboard')} sx={{ fontWeight: 700, flexShrink: 0 }}>
            Back
          </Button>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0, display: { xs: 'none', lg: 'flex' } }}>
            <Box component="img" src={logo} alt="" sx={{ width: 26, height: 26 }} />
            <Typography sx={{ fontWeight: 800 }}>Project Management</Typography>
          </Stack>

          <Stack direction="row" spacing={0.5} sx={{ flex: 1, flexWrap: 'wrap', rowGap: 0.5, justifyContent: { lg: 'center' } }}>
            {LINKS.filter((l) => !l.perm || can(l.perm)).map(({ to, label, icon: Icon, end }) => {
              const active = end ? location.pathname === to : location.pathname.startsWith(to);
              return (
                <Button
                  key={to}
                  component={NavLink}
                  to={to}
                  end={end}
                  startIcon={<Icon fontSize="small" />}
                  sx={{
                    fontWeight: 700,
                    color: active ? '#fff' : 'text.secondary',
                    background: active ? BRAND_GRADIENT : 'transparent',
                    '&:hover': { background: active ? BRAND_GRADIENT : undefined, bgcolor: active ? undefined : 'action.hover' },
                  }}
                >
                  {label}
                </Button>
              );
            })}
          </Stack>

          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
            <ActiveTimersIndicator />
            <Tooltip title={mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}>
              <IconButton onClick={toggleMode} aria-label="Toggle color mode">{mode === 'light' ? <DarkModeRounded /> : <LightModeRounded />}</IconButton>
            </Tooltip>
            <UserMenu user={user} avatarInitial={avatarInitial} go={go} onLogout={handleLogout} size={34} />
          </Stack>
        </Stack>
      </Box>
      <Outlet />
    </Box>
  );
}
